import * as React from 'react';
import Image from 'antd/lib/image';
import Spin from 'antd/lib/spin';
import { antGrey } from '../styles/globalStyles';

// Needed because checklist attachments are uploaded to S3 asynchronously
// the image URL may 404 briefly while the backend is still uploading after the item was created.
interface RetryableChecklistAttachmentImageProps {
    src: string;
    alt: string;
    width: number;
    height: number;
    maxAttempts?: number;
    retryDelayMs?: number;
}

const RetryableChecklistAttachmentImage: React.FC<RetryableChecklistAttachmentImageProps> = ({
    src,
    alt,
    width,
    height,
    maxAttempts = 3,
    retryDelayMs = 5000,
}) => {
    const [attempt, setAttempt] = React.useState(0);
    const [permanentFail, setPermanentFail] = React.useState(false);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, []);

    const handleError = () => {
        if (attempt + 1 < maxAttempts) {
            timeoutRef.current = setTimeout(() => setAttempt((a) => a + 1), retryDelayMs);
        } else {
            setPermanentFail(true);
        }
    };

    const containerStyle: React.CSSProperties = {
        width,
        height,
        borderRadius: 4,
        border: `1px solid ${antGrey.grey4}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: antGrey.grey2,
    };

    if (permanentFail) {
        return <div style={{ ...containerStyle, fontSize: 11, color: antGrey.grey7 }}>Failed to load</div>;
    }

    return (
        <Image
            key={attempt}
            src={src}
            alt={alt}
            width={width}
            height={height}
            placeholder={
                <div style={containerStyle}>
                    <Spin size="small" />
                </div>
            }
            onError={handleError}
            style={{
                objectFit: 'cover',
                borderRadius: 4,
                border: `1px solid ${antGrey.grey4}`,
                cursor: 'pointer',
            }}
        />
    );
};

export default RetryableChecklistAttachmentImage;
